// useSelfOnboardingConfig.ts
import {useQueryClient} from "@tanstack/react-query";
import {SelfOnboardingConfigDTO} from "@/shared/api/model";
// Import Orval-generated clients and types
import { 
    useCanSelfRegister as useOrvalCanSelfRegister,
    useGetActiveConfig as useOrvalGetActiveConfig,
    useGetAllConfigs as useOrvalGetAllConfigs,
    useCreateConfig as useOrvalCreateConfig,
    useUpdateConfig as useOrvalUpdateConfig,
    useDeleteConfig as useOrvalDeleteConfig,
    useSetActiveConfig as useOrvalSetActiveConfig
} from "@/shared/api/client/self-onboarding-configs";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";

export function useGetAllConfigs() {
    // Use the Orval-generated hook
    const result = useOrvalGetAllConfigs();
    
    // Orval returns data directly
    return result as unknown as UseQueryResult<SelfOnboardingConfigDTO[], Error>;
}

export function useGetActiveConfig() {
    // Use the Orval-generated hook
    const result = useOrvalGetActiveConfig();
    
    // Orval returns data directly
    return result as unknown as UseQueryResult<SelfOnboardingConfigDTO, Error>;
}

export function useCanSelfRegister() {
    // Use the Orval-generated hook
    const result = useOrvalCanSelfRegister();
    
    // Orval returns data directly
    return [{
        isLoading: result.isLoading,
        isError: result.isError,
        data: result.data
    }];
}

export function useCreateConfig() {
    const queryClient = useQueryClient();
    const orvalMutation = useOrvalCreateConfig({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: ['selfOnboardingConfigs']});
            }
        }
    });

    // Return the same structure that components expect
    return {
        isLoading: orvalMutation.isPending,
        mutateAsync: async (config: SelfOnboardingConfigDTO) => {
            const result = await orvalMutation.mutateAsync({ data: config });
            return result;
        }
    };
}

export function useUpdateConfig() {
    const queryClient = useQueryClient();
    const orvalMutation = useOrvalUpdateConfig({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: ['selfOnboardingConfigs']});
            }
        }
    });

    // Return the same structure that components expect
    return {
        isLoading: orvalMutation.isPending,
        mutateAsync: async (config: SelfOnboardingConfigDTO) => {
            const result = await orvalMutation.mutateAsync({ data: config });
            return result;
        }
    };
}

export function useDeleteConfig() {
    const queryClient = useQueryClient();
    const orvalMutation = useOrvalDeleteConfig({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: ['selfOnboardingConfigs']});
            }
        }
    });

    // Return the same structure that components expect
    return {
        isLoading: orvalMutation.isPending,
        mutateAsync: async (id: number) => {
            await orvalMutation.mutateAsync({ id });
            return;
        }
    };
}

export function useSetActiveConfig() {
    const queryClient = useQueryClient();
    const orvalMutation = useOrvalSetActiveConfig({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: ['selfOnboardingConfigs', 'activeSelfOnboardingConfig']});
            }
        }
    });

    // Return the same structure that components expect
    return {
        isLoading: orvalMutation.isPending,
        mutateAsync: async (id: number) => {
            const result = await orvalMutation.mutateAsync({ id });
            return result;
        }
    };
}
